export const ANIMATION_TYPES = [
  'none',
  'fadeIn',
  'slideUp',
  'slideDown',
  'slideLeft',
  'slideRight',
  'zoomIn',
  'zoomOut',
  'flipUp',
  'bounceIn',
  'blurIn',
  'revealLeft',
  'revealRight',
  'revealUp',
  'revealDown',
] as const;

export type AnimationType = (typeof ANIMATION_TYPES)[number];

export const ANIMATION_EASINGS = ['ease-out', 'ease-in', 'ease-in-out', 'linear'] as const;

export type AnimationEasing = (typeof ANIMATION_EASINGS)[number];

export const ANIMATION_TYPE_LABELS: Record<AnimationType, string> = {
  none: 'None',
  fadeIn: 'Fade In',
  slideUp: 'Slide Up',
  slideDown: 'Slide Down',
  slideLeft: 'Slide Left',
  slideRight: 'Slide Right',
  zoomIn: 'Zoom In',
  zoomOut: 'Zoom Out',
  flipUp: 'Flip Up',
  bounceIn: 'Bounce In',
  blurIn: 'Blur In',
  revealLeft: 'Reveal Left',
  revealRight: 'Reveal Right',
  revealUp: 'Reveal Up',
  revealDown: 'Reveal Down',
};

export const ANIMATION_DEFAULTS = {
  animationType: 'none' as AnimationType,
  animationDuration: 600,
  animationDelay: 0,
  animationEasing: 'ease-out' as AnimationEasing,
} as const;

// Hover effects
export const HOVER_EFFECT_TYPES = ['none', 'scale', 'lift', 'glow', 'brighten'] as const;
export type HoverEffectType = (typeof HOVER_EFFECT_TYPES)[number];
export const HOVER_EFFECT_LABELS: Record<HoverEffectType, string> = {
  none: 'None',
  scale: 'Scale Up',
  lift: 'Lift',
  glow: 'Glow',
  brighten: 'Brighten',
};
export const HOVER_INTENSITY_DEFAULTS: Record<Exclude<HoverEffectType, 'none'>, number> = {
  scale: 105,
  lift: 4,
  glow: 20,
  brighten: 110,
};

export const HOVER_INTENSITY_CONFIG: Record<
  Exclude<HoverEffectType, 'none'>,
  { label: string; min: number; max: number; step: number; suffix: string; default: number }
> = {
  scale: { label: 'Scale', min: 100, max: 150, step: 1, suffix: '%', default: 105 },
  lift: { label: 'Lift', min: 1, max: 20, step: 1, suffix: 'px', default: 4 },
  glow: { label: 'Glow', min: 5, max: 60, step: 1, suffix: 'px', default: 20 },
  brighten: { label: 'Brighten', min: 100, max: 150, step: 1, suffix: '%', default: 110 },
};

export const HOVER_DEFAULTS = {
  hoverEffect: 'none' as HoverEffectType,
  hoverTransitionDuration: 300,
} as const;

// Image continuous animations
export const IMAGE_CONTINUOUS_TYPES = ['none', 'kenBurns', 'slowZoom', 'pulse'] as const;
export type ImageContinuousType = (typeof IMAGE_CONTINUOUS_TYPES)[number];
export const IMAGE_CONTINUOUS_LABELS: Record<ImageContinuousType, string> = {
  none: 'None',
  kenBurns: 'Ken Burns',
  slowZoom: 'Slow Zoom',
  pulse: 'Pulse',
};

export const CONTINUOUS_DEFAULTS: Record<
  Exclude<ImageContinuousType, 'none'>,
  { speed: number; intensity: number }
> = {
  kenBurns: { speed: 15, intensity: 10 },
  slowZoom: { speed: 12, intensity: 8 },
  pulse: { speed: 3, intensity: 15 },
};

export const CONTINUOUS_SPEED_CONFIG: Record<
  Exclude<ImageContinuousType, 'none'>,
  { min: number; max: number; step: number }
> = {
  kenBurns: { min: 3, max: 30, step: 1 },
  slowZoom: { min: 3, max: 30, step: 1 },
  pulse: { min: 1, max: 10, step: 1 },
};

export interface AnimationProps {
  animationType: AnimationType;
  animationDuration: number;
  animationDelay: number;
  animationEasing: AnimationEasing;
  hoverEffect: HoverEffectType;
  hoverTransitionDuration: number;
  hoverIntensity: number;
  staggerDelay: number;
  continuousAnimation: ImageContinuousType;
  continuousSpeed: number;
  continuousIntensity: number;
}

export const CONTINUOUS_INTENSITY_CONFIG: Record<
  Exclude<ImageContinuousType, 'none'>,
  { label: string; min: number; max: number; step: number; suffix: string }
> = {
  kenBurns: { label: 'Intensity', min: 1, max: 20, step: 1, suffix: '' },
  slowZoom: { label: 'Intensity', min: 1, max: 20, step: 1, suffix: '' },
  pulse: { label: 'Intensity', min: 5, max: 50, step: 1, suffix: '' },
};
